<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import Dropdown from "primevue/dropdown";

const { t } = useI18n();

const emit = defineEmits(["update:modelValue", "change"]);

const props = withDefaults(
  defineProps<{
    options: { option: string; value: string | number | boolean }[];
    width?: string;
    height?: string;
    radius?: string;
    background?: string;
    placeholder?: string;
    value?: any;
    isEmpty?: boolean;
    loading?: boolean;
    disabled?: boolean;
  }>(),
  {
    width: "100%",
    height: "40px",
    radius: "6px",
    background: "#fff",
    isEmpty: true,
    disabled: false,
  }
);

const internalValue = ref<string | number | null>(null);

const internalOptions = computed(() => {
  const optionEmpty = {
    label: t("common.placeholders.select"),
    value: "empty",
  };

  const optionsFormatted = (props.options || []).map((item) => ({
    label: item.option,
    value: item.value,
  }));

  return props.isEmpty ? [optionEmpty, ...optionsFormatted] : optionsFormatted;
});

function update(data: { originalEvent: Event; value: any }) {
  internalValue.value = data.value === "empty" ? null : data.value;
  emit("update:modelValue", internalValue.value);
  emit("change", internalValue.value);
}

watchEffect(() => {
  if (props.value !== undefined) internalValue.value = props.value;
});
</script>

<template>
  <div class="base-select">
    <div v-if="$slots.label" class="label">
      <slot name="label" />
    </div>
    <div class="content" :class="{ error: $slots.error }">
      <div v-if="$slots.prefix" class="prefix">
        <slot name="prefix" />
      </div>
      <div class="select" :class="{ pref: $slots.prefix, suf: $slots.sufix }">
        <Dropdown
          v-if="!loading"
          class="custom-dropdown"
          :class="{
            disabled: disabled,
          }"
          v-bind="$attrs"
          v-model="internalValue"
          :options="internalOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="
            placeholder ? placeholder : t('common.placeholders.select')
          "
          :disabled="disabled"
          @change="update"
        />
      </div>
      <div v-if="$slots.sufix" class="sufix">
        <slot name="sufix" />
      </div>
    </div>
    <div v-if="$slots.error" class="error-message">
      <slot name="error" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
$width: v-bind("width");
$height: v-bind("height");
$radius: v-bind("radius");
$background: v-bind("background");

.base-select {
  display: flex;
  flex-direction: column;
  width: $width;
  gap: 2px;

  .label {
    font-size: 0.875rem;
    margin-block-end: 0.2rem;
    margin-inline: 0.5rem;
  }

  .content {
    display: flex;
    flex-direction: row;
    width: calc(100% - (1px * 2));
    height: calc($height + 4px);
    justify-content: center;
    align-items: center;
    border: 1px solid #d9d9d9;
    border-radius: $radius;
    background: $background;

    &.error {
      border-color: red;
    }
  }

  .error-message {
    text-align: end;
    font-size: 0.8rem;
    padding-inline: $radius;
    color: red;
  }

  .prefix,
  .sufix {
    user-select: none;
    display: flex;
    flex-direction: column;
    width: $height;
    height: $height;
    align-items: center;
    justify-content: center;
  }

  .select {
    cursor: pointer;
    flex: 1;
    height: calc($height + 2px);
    outline: none;

    &.pref {
      padding-inline-start: 0;
    }

    &.suf {
      padding-inline-end: 0;
    }

    :deep(.p-placeholder) {
      outline: none;
      color: var(--text-medium);
    }

    :focus-visible {
      outline: none;
    }
  }

  .custom-dropdown {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 26px);
    height: 100%;
    background: white;
    padding-inline: 13px;
    border-radius: $radius;
    
    &.disabled {
      cursor: not-allowed;
      background: var(--white-off);
      border-color: var(--border-off);
    }
  }
}

.options {
  background: white;
  padding-inline: 10px;
  padding-block: 0.5rem;
  border-block-end: 1px solid #d9d9d9;

  &.empty {
    color: var(--text-off);
  }

  &:hover {
    background: var(--primary);
    color: var(--primary-color);
  }
}
</style>
